import { last } from 'lodash';
import moment from 'moment-timezone';

import { Nullable } from '@pimm/base';
import { tempValue } from '@app/utils/string-formatter';
import {
  AlarmLevel as AlarmLevelEnum,
  ChartMetricDto,
  ChartStatisticsDto,
  EquipmentInstancesDto,
  EventPriority,
  LinkedInstanceChartDataDto,
  ThresholdsDto,
} from '@pimm/services/lib/store-equipment';
import { BatteryLevel, EquipmentListFilter } from './types';

export { tempValue };
export const SERVER_TZ = 'America/Los_Angeles';

// ---------------------------------------------------------------------------
// Threshold color helpers (hex — used by sensor-table column renderers)
// ---------------------------------------------------------------------------

const _NEUTRAL = '#374151';
const _AMBER   = '#D97706';
const _RED     = '#DC2626';

export const UPTIME_RED    = 80;
export const UPTIME_YELLOW = 90;
export const DATA_RED      = 95;
export const DATA_YELLOW   = 99;
export const IN_TEMP_RED   = 70;
export const IN_TEMP_YELLOW = 90;

export const uptimeColor = (p: number): string => (p < UPTIME_RED ? _RED : p < UPTIME_YELLOW ? _AMBER : _NEUTRAL);
export const dataColor   = (p: number): string => (p < DATA_RED  ? _RED : p < DATA_YELLOW   ? _AMBER : _NEUTRAL);
export const itpColor    = (p: number): string => (p < IN_TEMP_RED ? _RED : p < IN_TEMP_YELLOW ? _AMBER : _NEUTRAL);

export const tempColor = (t: number, th?: ThresholdsDto): string => {
  if (!th || th.LowerYellow == null || th.UpperYellow == null) return _NEUTRAL;
  if (t < th.LowerYellow || t > th.UpperYellow) return _RED;
  if ((th.LowerGreen != null && t < th.LowerGreen) || (th.UpperGreen != null && t > th.UpperGreen)) return _AMBER;
  return _NEUTRAL;
};

export const fmt1 = (n: number): string => n.toFixed(1);

export const fmtLastSeen = (iso?: string): string => {
  if (!iso) return '—';
  const then = moment.tz(iso, SERVER_TZ);
  if (!then.isValid()) return '—';
  return then.fromNow();
};

export const fmtDuration = (min: number): string => {
  if (min <= 0) return '—';
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  const m = Math.round(min % 60);
  if (d > 0) return m > 0 ? `${d}d ${h}h ${m}m` : `${d}d ${h}h`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
};

export { ThresholdColors } from './consts';

// ---------------------------------------------------------------------------
// Battery label helpers
// ---------------------------------------------------------------------------

const TELTONIKA_MODELS = ['EYE Sensor (BTSMP1)', 'EYE Sensor (Mesh)', 'PUCK T-Probe'];
const JOINTIFF_MODELS = ['TSU Internal', 'RSU External'];

export const getBatteryLabel = (level: BatteryLevel, model?: string): string => {
  if (TELTONIKA_MODELS.includes(model ?? '')) {
    const map: Record<BatteryLevel, string> = {
      ok: 'store_equipment:battery_good',
      info: 'store_equipment:battery_low',
      warning: 'store_equipment:battery_order_soon',
      alarm: 'store_equipment:battery_order_now',
      dead: 'store_equipment:battery_dead_order',
    };
    return map[level];
  }
  if (JOINTIFF_MODELS.includes(model ?? '')) {
    const map: Record<BatteryLevel, string> = {
      ok: 'store_equipment:battery_good',
      info: 'store_equipment:battery_low',
      warning: 'store_equipment:battery_replace_soon',
      alarm: 'store_equipment:battery_replace_now',
      dead: 'store_equipment:battery_dead_replace',
    };
    return map[level];
  }
  const defaults: Record<BatteryLevel, string> = {
    ok: 'store_equipment:battery_good',
    info: 'store_equipment:battery_low',
    warning: 'store_equipment:battery_replace_soon',
    alarm: 'store_equipment:battery_critical',
    dead: 'store_equipment:battery_dead',
  };
  return defaults[level];
};

export const getBatteryNote = (model?: string): string | undefined => {
  if (TELTONIKA_MODELS.includes(model ?? '')) return 'store_equipment:battery_note_teltonika';
  if (JOINTIFF_MODELS.includes(model ?? '')) return 'store_equipment:battery_note_jointiff';
  return undefined;
};

export const getSignalState = (alarmLevel?: AlarmLevelEnum): { color: string; text: string } => {
  switch (alarmLevel) {
    case AlarmLevelEnum.Alarm:
      return { color: 'error.500', text: 'Weak' };
    case AlarmLevelEnum.Warning:
      return { color: 'warning.400', text: 'Ok' };
    default:
      return { color: 'success.600', text: 'Strong' };
  }
};

export const getEventPriorityColor = (priority?: EventPriority, temperature?: boolean) => {
  switch (priority) {
    case EventPriority.L1:
      return temperature ? 'success.600' : 'yellow.600';
    case EventPriority.L2:
      return temperature ? 'yellow.400' : 'warning.500';
    case EventPriority.L3:
      return 'error.500';
    case EventPriority.L4:
      return 'black';
    default:
      return undefined;
  }
};

const hasActiveEvent = (sensor?: { IsOnline?: boolean; EventPriority?: string }): boolean =>
  !!sensor && !!sensor.IsOnline && sensor.EventPriority?.toLowerCase() !== 'none';

export const matchesEquipmentFilter = (filter: EquipmentListFilter, equipment: EquipmentInstancesDto): boolean => {
  switch (filter) {
    case 'Temperature': return hasActiveEvent(equipment.TemperatureMetrics?.Temperature);
    case 'Door':        return hasActiveEvent(equipment.DoorMetrics?.Door);
    case 'Battery':
      return hasActiveEvent(equipment.TemperatureMetrics?.BatteryVoltage) ||
             hasActiveEvent(equipment.DoorMetrics?.BatteryVoltage);
    case 'Offline':
      return (!!equipment.TemperatureMetrics?.Temperature && !equipment.TemperatureMetrics.Temperature.IsOnline) ||
             (!!equipment.DoorMetrics?.Door && !equipment.DoorMetrics.Door.IsOnline);
    default: return true;
  }
};

export const countOfflineSensors = (equipment: EquipmentInstancesDto): number =>
  (!!equipment.TemperatureMetrics?.Temperature && !equipment.TemperatureMetrics.Temperature.IsOnline ? 1 : 0) +
  (!!equipment.DoorMetrics?.Door && !equipment.DoorMetrics.Door.IsOnline ? 1 : 0);

export function getSensorGroupBySensorName(value: string) {
  if (value.toLowerCase().includes('walk')) {
    return 'Walk-in';
  } else if (value.toLowerCase().includes('cooler')) {
    return 'Cooler';
  } else if (value.toLowerCase().includes('freezer')) {
    return 'Freezer';
  } else {
    return 'Other Temps';
  }
}

export function formatViolationTrigger(value: number, upper?: boolean) {
  return `${upper ? '>' : '<'}${tempValue(value)}`;
}

export function getViolationTriggerLabel(eventType: string, triggerValue?: number | null, upper?: boolean): string {
  switch (eventType.toLowerCase()) {
    case 'temperature':
      return typeof triggerValue === 'number' ? formatViolationTrigger(triggerValue, upper) : '';
    case 'door open':
      return 'Open';
    case 'offline':
      return 'Offline';
    case 'low battery':
      return 'Low Battery';
    default:
      return '';
  }
}

export function getThresholdOffset(value: number, min: number, max: number) {
  return (max - value) / (max - min);
}

export const parseTimeToSeconds = (time?: string): number => {
  if (!time || time === '00:00:00') return 0; // Handle null and '00:00:00'
  // Split the time string into hours, minutes, and seconds
  const [hours, minutes, seconds] = time.split(':').map(Number);
  // Convert to total seconds
  return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
};

export const parseDuration = (duration: string | undefined): string[] => {
  const timeRegex = /^(\d{1,2})(?:\.(\d{1,2}))?:([0-5]\d):([0-5]\d)$/;
  if (duration && timeRegex.test(duration)) {
    const match = duration.match(timeRegex);
    if (match) {
      const [, daysOrHours, hoursPart, minutes, seconds] = match;
      const days = hoursPart ? parseInt(daysOrHours, 10) : 0;
      const hours = hoursPart ? parseInt(hoursPart, 10) : parseInt(daysOrHours, 10);
      const formattedDays = days.toString().padStart(2, '0');
      const formattedHours = hours.toString().padStart(2, '0');
      return [formattedDays, formattedHours, minutes];
    }
  }
  return [];
};

export const createMetricsets = (_data?: ChartMetricDto[], maxGapMs?: number, bucketMs = FIVE_MIN_MS) => {
  const metrics = _data ?? [];
  const _metrics: { startTime: Nullable<number>; endTime: Nullable<number>; value: number }[] = [];
  let currentEvent: { startTime: Nullable<number>; endTime: Nullable<number>; value: number } | undefined;
  let prevTimestamp: number | undefined;

  const lastMetric = last(metrics);

  metrics?.forEach(item => {
    const timestamp = new Date(item.Timestamp as string).getTime();
    const value = item.Value ?? 0;
    const gapExceeded = maxGapMs != null && prevTimestamp != null && timestamp - prevTimestamp > maxGapMs;

    if (!currentEvent || value !== currentEvent.value || gapExceeded) {
      if (currentEvent) {
        currentEvent.endTime = (prevTimestamp ?? timestamp) + bucketMs;
        _metrics.push(currentEvent);
      }
      currentEvent = { value, startTime: timestamp, endTime: null };
    }

    prevTimestamp = timestamp;
  });

  // Close the last event
  if (currentEvent && lastMetric) {
    currentEvent.endTime = new Date(lastMetric.Timestamp as string).getTime();
    _metrics.push(currentEvent);
  }

  return _metrics;
};

// ---------------------------------------------------------------------------
// Zoom helpers
// ---------------------------------------------------------------------------

// Temperature data is bucketed at 5-minute boundaries server-side.
export const FIVE_MIN_MS = 5 * 60 * 1000;

export const snapToFiveMinutes = (ms: number): number => Math.round(ms / FIVE_MIN_MS) * FIVE_MIN_MS;

// ---------------------------------------------------------------------------
// Excel export types & builders
// ---------------------------------------------------------------------------

export type EquipmentExportRow = {
  Timestamp: string;
  'Temp (°F)': number | '';
  'Signal (0=Offline, 1=Online)': number | '';
  'Door (1=Closed, 2=Open)': number | '';
};

const lastKnownValue = (data: any[] | undefined, targetMs: number): number | undefined => {
  if (!data?.length) return undefined;
  let val: number | undefined;
  for (const point of data) {
    if (!point.Timestamp) continue;
    const t = new Date(point.Timestamp).getTime();
    if (t > targetMs) break;
    if (typeof point.Value === 'number') val = point.Value;
  }
  return val;
};

export const buildEquipmentExportRows = (chartData: LinkedInstanceChartDataDto[] | undefined): EquipmentExportRow[] => {
  const groups = (chartData ?? []) as LinkedInstanceChartDataDto[];
  const linkedTemperature = groups.find(g => !!g?.Temperature);
  const linkedDoor = groups.find(g => !!g?.Door);

  const tempData = linkedTemperature?.Temperature?.Data ?? [];
  const signalData = linkedTemperature?.RealTimeStatus?.Data ?? [];
  const doorData = linkedDoor?.Door?.Data ?? [];
  const hasDoor = doorData.length > 0;

  return (tempData as any[])
    .filter((point: any) => !!point.Timestamp)
    .map((point: any) => {
      const ts = new Date(point.Timestamp as string).getTime();
      const signal = lastKnownValue(signalData as any[], ts);
      const door = hasDoor ? lastKnownValue(doorData as any[], ts) : undefined;

      return {
        Timestamp: moment(point.Timestamp as string).format('YYYY-MM-DD HH:mm'),
        'Temp (°F)': typeof point.Value === 'number' ? point.Value : '',
        'Signal (0=Offline, 1=Online)': typeof signal === 'number' ? signal : '',
        'Door (1=Closed, 2=Open)': typeof door === 'number' ? door : '',
      };
    });
};

export const buildExportFileName = (
  storeName: string | undefined,
  equipmentName: string | undefined,
  generatedDate: Date = new Date(),
): string => {
  const sanitize = (s: string | undefined, fallback: string) => {
    const cleaned = (s ?? '')
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    return cleaned || fallback;
  };
  const safeStore = sanitize(storeName, 'site');
  const safeEquip = sanitize(equipmentName, 'equipment');
  const safeDate = moment(generatedDate).format('YYYY-MM-DD');
  return `${safeStore}_${safeEquip}_${safeDate}`;
};

export type EquipmentInfoRow = { Label: string; Value: string };

export type EquipmentInfoSheetInput = {
  customerName?: string;
  storeName?: string;
  address?: string;
  equipmentName?: string;
  temperatureSensor?: { Manufacturer?: string; Model?: string; Serial?: string };
  doorSensor?: { Manufacturer?: string; Model?: string; Serial?: string };
  dateRangeLabel: string;
  generatedBy?: string;
  generatedAt: string;
  stats?: { Average?: number; Min?: number; Max?: number; StdDev?: number; InTempPct?: number; DataUploadPct?: number; UpTimePct?: number };
};

export const buildEquipmentInfoRows = (input: EquipmentInfoSheetInput): EquipmentInfoRow[] => {
  const rows: EquipmentInfoRow[] = [];
  const push = (label: string, value: string | number | undefined | null) => {
    rows.push({ Label: label, Value: value == null || value === '' ? '—' : String(value) });
  };

  push('Customer', input.customerName);
  push('Store', input.storeName);

  if (input.address) {
    push('Address', input.address);
  }

  push('Equipment', input.equipmentName);

  const formatSensor = (s?: { Manufacturer?: string; Model?: string; Serial?: string }) =>
    s ? `${s.Model ?? '—'} · ${s.Serial ?? '—'}` : '';
  if (input.temperatureSensor) push('Temperature Sensor', formatSensor(input.temperatureSensor));
  if (input.doorSensor) push('Door Sensor', formatSensor(input.doorSensor));

  push('Date Range', input.dateRangeLabel);
  push('Generated By', input.generatedBy);
  push('Generated At', input.generatedAt);

  if (input.stats) {
    rows.push({ Label: '', Value: '' });
    rows.push({ Label: 'Statistics', Value: '' });
    const fmtTemp = (v?: number) => (typeof v === 'number' ? `${v.toFixed(2)}°F` : '—');
    const fmtPct = (v?: number) => (typeof v === 'number' ? `${v.toFixed(1)}%` : '—');
    const fmtNum = (v?: number) => (typeof v === 'number' ? v.toFixed(2) : '—');
    push('Average', fmtTemp(input.stats.Average));
    push('Min', fmtTemp(input.stats.Min));
    push('Max', fmtTemp(input.stats.Max));
    push('Std Dev', fmtNum(input.stats.StdDev));
    push('In-Temp %', fmtPct(input.stats.InTempPct));
    push('Data Upload %', fmtPct(input.stats.DataUploadPct));
    push('Uptime %', fmtPct(input.stats.UpTimePct));
  }

  return rows;
};

export type EventLogExportRow = {
  'Event Type': string;
  'Violation Trigger': string;
  'Event Severity': string;
  'Start Time': string;
  'End Time': string;
  Duration: string;
};

export const buildEventLogRows = (
  storeEvents:
    | {
        Equipment?: Array<{
          TemperatureEvents?: any[];
          RealTimeStatusEvents?: any[];
          DoorEvents?: any[];
          BatteryEvents?: any[];
        }>;
      }
    | undefined,
): EventLogExportRow[] => {
  const groups: { eventType: string; events: any[] }[] = [
    { eventType: 'Temperature', events: [] },
    { eventType: 'Offline', events: [] },
    { eventType: 'Door Open', events: [] },
    { eventType: 'Low Battery', events: [] },
  ];
  (storeEvents?.Equipment ?? []).forEach(eq => {
    groups[0].events.push(...(eq.TemperatureEvents ?? []));
    groups[1].events.push(...(eq.RealTimeStatusEvents ?? []));
    groups[2].events.push(...(eq.DoorEvents ?? []));
    groups[3].events.push(...(eq.BatteryEvents ?? []));
  });

  const rows: (EventLogExportRow & { _sortMs: number })[] = [];
  groups.forEach(g => {
    g.events.forEach((ev: any) => {
      const start = ev?.StartTime ? new Date(ev.StartTime) : null;
      const end = ev?.EndTime ? new Date(ev.EndTime) : null;
      const minutes = start && end ? Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000)) : 0;
      const hrs = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const duration = start && end ? `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}` : '';

      const trigger = getViolationTriggerLabel(g.eventType, ev?.ViolationTriggerValue, ev?.ThresholdMode === 'Upper');

      rows.push({
        'Event Type': g.eventType,
        'Violation Trigger': trigger || '—',
        'Event Severity': ev?.Priority ?? '—',
        'Start Time': start ? moment(start).format('YYYY-MM-DD HH:mm') : '—',
        'End Time': end ? moment(end).format('YYYY-MM-DD HH:mm') : '—',
        Duration: duration || '—',
        _sortMs: start ? start.getTime() : 0,
      });
    });
  });

  return rows.sort((a, b) => b._sortMs - a._sortMs).map(({ _sortMs, ...rest }) => rest);
};

// ---------------------------------------------------------------------------
// Zoom-aware chart-data clamp
// ---------------------------------------------------------------------------

export type ClampThresholds = {
  LowerGreen?: number;
  UpperGreen?: number;
};

const clipMetricsByRange = (data: any[] | undefined, startMs: number, endMs: number): any[] => {
  if (!data?.length) return data ?? [];
  return data.filter((p: any) => {
    if (!p.Timestamp) return false;
    const t = new Date(p.Timestamp as string).getTime();
    return t >= startMs && t <= endMs;
  });
};

export const clampChartDataByRange = (
  chartData: LinkedInstanceChartDataDto[] | undefined,
  startMs: number,
  endMs: number,
  thresholds?: ClampThresholds,
): LinkedInstanceChartDataDto[] => {
  if (!chartData?.length) return [];
  return chartData.map(group => {
    const next: LinkedInstanceChartDataDto = { ...group };
    if (group.Temperature) {
      const data = clipMetricsByRange(group.Temperature.Data, startMs, endMs);
      const signalData = clipMetricsByRange(group.RealTimeStatus?.Data, startMs, endMs);
      const vals = data.map(p => (typeof p.Value === 'number' ? p.Value : null)).filter((v): v is number => v != null);
      const baseStats = group.Temperature.Statistics ?? {};
      let Average: number | undefined = baseStats.Average;
      let Min: number | undefined = baseStats.Min;
      let Max: number | undefined = baseStats.Max;
      let StdDev: number | undefined = baseStats.StdDev;
      let InTempPct: number | undefined = baseStats.InTempPct;
      let DataUploadPct: number | undefined = baseStats.DataUploadPct;
      if (vals.length > 0) {
        const total = vals.reduce((a, b) => a + b, 0);
        Average = total / vals.length;
        Min = Math.min(...vals);
        Max = Math.max(...vals);
        const variance = vals.reduce((acc, v) => acc + (v - Average!) ** 2, 0) / vals.length;
        StdDev = Math.sqrt(variance);
        if (typeof thresholds?.LowerGreen === 'number' && typeof thresholds?.UpperGreen === 'number') {
          const lo = thresholds.LowerGreen;
          const hi = thresholds.UpperGreen;
          const inRange = vals.filter(v => v > lo && v <= hi).length;
          InTempPct = (inRange / vals.length) * 100;
        }
      }
      const signalVals = signalData.map(p => (typeof p.Value === 'number' ? p.Value : null)).filter((v): v is number => v != null);
      if (signalVals.length > 0) {
        const online = signalVals.filter(v => v === 1).length;
        DataUploadPct = (online / signalVals.length) * 100;
      }
      next.Temperature = {
        ...group.Temperature,
        Data: data,
        Statistics: { ...baseStats, Average, Min, Max, StdDev, InTempPct, DataUploadPct },
      };
    }
    if (group.RealTimeStatus) {
      const clippedRtData = clipMetricsByRange(group.RealTimeStatus.Data, startMs, endMs);
      const rtVals = clippedRtData
        .map(p => (typeof p.Value === 'number' ? p.Value : null))
        .filter((v): v is number => v != null);
      let UpTimePct: number | undefined = group.RealTimeStatus.Statistics?.UpTimePct;
      if (rtVals.length > 0) {
        UpTimePct = (rtVals.filter(v => v === 1).length / rtVals.length) * 100;
      }
      next.RealTimeStatus = {
        ...group.RealTimeStatus,
        Data: clippedRtData,
        Statistics: { ...group.RealTimeStatus.Statistics, UpTimePct },
      };
    }
    if (group.Door) {
      next.Door = {
        ...group.Door,
        Data: clipMetricsByRange(group.Door.Data, startMs, endMs),
      };
    }
    return next;
  });
};

const overlapsRange = (startTime: string | undefined, endTime: string | undefined, startMs: number, endMs: number): boolean => {
  if (!startTime) return false;
  const evStart = new Date(startTime).getTime();
  const evEnd = endTime ? new Date(endTime).getTime() : Infinity;
  return evStart <= endMs && evEnd >= startMs;
};

export const clampStoreEventsByRange = <
  T extends
    | {
        Equipment?: Array<{
          TemperatureEvents?: any[];
          RealTimeStatusEvents?: any[];
          DoorEvents?: any[];
          BatteryEvents?: any[];
        }>;
      }
    | undefined,
>(
  storeEvents: T,
  startMs: number,
  endMs: number,
): T => {
  if (!storeEvents) return storeEvents;
  const clampList = (arr: any[] | undefined) => (arr ?? []).filter(ev => overlapsRange(ev?.StartTime, ev?.EndTime, startMs, endMs));
  return {
    ...storeEvents,
    Equipment: (storeEvents.Equipment ?? []).map((eq: any) => ({
      ...eq,
      TemperatureEvents: clampList(eq?.TemperatureEvents),
      RealTimeStatusEvents: clampList(eq?.RealTimeStatusEvents),
      DoorEvents: clampList(eq?.DoorEvents),
      BatteryEvents: clampList(eq?.BatteryEvents),
    })),
  } as T;
};

export const buildChartStatTiles = (statistics?: ChartStatisticsDto, realTimeStatistics?: ChartStatisticsDto) => [
  { title: 'store_equipment:avg', value: tempValue(statistics?.Average) ?? '-' },
  { title: 'store_equipment:min', value: tempValue(statistics?.Min) ?? '-' },
  { title: 'store_equipment:max', value: tempValue(statistics?.Max) ?? '-' },
  { title: 'store_equipment:std_dev_short', value: `${statistics?.StdDev?.toFixed(1) ?? '-'}` },
  { title: 'store_equipment:in_temp_tab', value: `${statistics?.InTempPct?.toFixed(1) ?? 0}%` },
  { title: 'store_equipment:data_upload', value: `${statistics?.DataUploadPct?.toFixed(1) ?? 0}%` },
  { title: 'store_equipment:uptime_tab', value: `${realTimeStatistics?.UpTimePct?.toFixed(1) ?? 0}%` },
];
